import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { ArrowRight } from "lucide-react";
import { CardiogrammIcon } from "@/components/icons/CardiogrammIcon";
import { StethoscopeIcon } from "@/components/icons/StethoscopeIcon";
import { PlanIcon } from "@/components/icons/PlanIcon";
import { isBusinessHours } from "@/utils/businessHours";
import { languageMap } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";
import { getHomepageUrl, getSupportPhone } from "@/config/market";

interface EmailCaptureStepProps {
  onEmailSubmit: (email: string) => Promise<void>;
  onSkip?: () => void;
  language: BookingLanguage;
}

const LiveBadge = React.memo<{ language?: BookingLanguage; phone: string }>(
  ({ language = "en-US", phone }) => {
    const { t } = useTranslation(undefined, { lng: languageMap[language] });
    const [isOpen, setIsOpen] = useState(isBusinessHours());

    // Update status every minute
    useEffect(() => {
      const interval = setInterval(() => {
        setIsOpen(isBusinessHours());
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }, []);

    return (
      <div className="flex items-center justify-center space-x-2 text-gray-700">
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="text-sm hover:underline transition-colors"
        >
          {phone}
        </a>

        {/* Live badge - only show when business is open */}
        {isOpen && (
          <div
            className="flex items-center space-x-1 text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              background: "#A4AF4588",
              color: "white",
            }}
          >
            {/* Pulsing dot */}
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{
                background: "linear-gradient(135deg, #A4AF45 0%, #8D9B16 100%)",
                boxShadow: "0 0 4px rgba(164, 175, 69, 0.6)",
              }}
            />
            <span>{t("common.live")}</span>
          </div>
        )}
      </div>
    );
  },
);

const EmailCaptureStep: React.FC<EmailCaptureStepProps> = React.memo(
  ({ onEmailSubmit, onSkip, language }) => {
    const { t } = useTranslation(undefined, { lng: languageMap[language] });
    const { market } = useMarket();
    // Both site-root links on this screen (logo and footer) go to the market's
    // own homepage, not the bare root that redirects to Switzerland (AHD-8451).
    const homepageUrl = getHomepageUrl(market, language);
    // The market's own line; none in WhatsApp-only markets (NL).
    const supportPhone = getSupportPhone(market);
    const [email, setEmail] = useState("");
    const [isVisible, setIsVisible] = useState(false);
    const [showError, setShowError] = useState(false);

    // Trigger fade-in animation on mount
    useEffect(() => {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }, []);

    const validateEmail = useCallback((email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }, []);

    const isValid = useMemo(() => validateEmail(email), [email, validateEmail]);

    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();

        // Show error if email is empty or invalid
        if (!email.trim() || !isValid) {
          setShowError(true);
          return;
        }

        setShowError(false);
        await onEmailSubmit(email);
      },
      [email, isValid, onEmailSubmit],
    );

    const handleSkip = useCallback(() => {
      if (!onSkip) return;
      onSkip();
    }, [onSkip]);

    // Clear error when user starts typing
    const handleEmailChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (showError) {
          setShowError(false);
        }
      },
      [showError],
    );

    // Component for background images with loading placeholder and blur effect
    const BackgroundImage = useMemo(
      () =>
        React.memo<{
          src: string;
          alt: string;
          className: string;
          hasBlur?: boolean;
          imageClassName?: string;
        }>(({ src, alt, className, hasBlur = false, imageClassName = "" }) => {
          const [imageLoaded, setImageLoaded] = useState(false);
          const [imageFailed, setImageFailed] = useState(false);

          const handleImageLoad = useCallback(() => {
            setImageLoaded(true);
          }, []);

          const handleImageError = useCallback(() => {
            setImageFailed(true);
            setImageLoaded(true);
          }, []);

          if (imageFailed) {
            return <div className={className} />;
          }

          return (
            <div className={className}>
              <img
                src={src}
                alt={alt}
                className={`
            w-full h-full object-cover rounded-xl shadow-lg
            transition-opacity duration-300 ease-out
            ${imageLoaded ? "opacity-100" : "opacity-0"}
            ${hasBlur ? "blur-sm" : ""}
            ${imageClassName}
          `}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
              />
            </div>
          );
        }),
      [],
    );

    // Component for logo with loading placeholder
    const LogoImage = useMemo(
      () =>
        React.memo<{ className: string; href: string }>(
          ({ className, href }) => {
            const [logoLoaded, setLogoLoaded] = useState(false);

            const handleLogoLoad = useCallback(() => {
              setLogoLoaded(true);
            }, []);

            return (
              <a
                href={href}
                target="_blank"
                className={`${className} relative cursor-pointer hover:opacity-80 transition-opacity block`}
                aria-label="Go to Ahead Health homepage"
              >
                <img
                  src="/ahead-logo.png"
                  alt="Ahead Logo"
                  className={`
            ${className}
            transition-opacity duration-300 ease-out
            ${logoLoaded ? "opacity-100" : "opacity-0"}
          `}
                  onLoad={handleLogoLoad}
                  loading="eager"
                />
              </a>
            );
          },
        ),
      [],
    );

    return (
      <div
        className={`min-h-screen transition-opacity duration-500 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(147, 197, 253, 0.3) 0%, rgba(147, 197, 253, 0.1) 40%, #f9fafb 70%)",
        }}
      >
        <div className="min-h-screen flex flex-col relative">
          {/* Logo - only visible on desktop, positioned absolutely */}
          <div className="absolute top-4 left-4 z-30 hidden sm:block">
            <LogoImage className="w-32 lg:w-36" href={homepageUrl} />
          </div>

          {/* Background images - only on larger screens */}
          <BackgroundImage
            src="/email-capture/top-left.webp"
            alt=""
            className="absolute top-12 left-72 w-32 lg:w-36 h-32 lg:h-36 hidden lg:block"
            hasBlur={true}
            imageClassName="blur-sm"
          />
          <BackgroundImage
            src="/email-capture/topleft-closer-to-modal.webp"
            alt=""
            className="absolute top-20 lg:left-8 xl:left-1/4 w-40 h-40 lg:w-48 lg:h-48 hidden lg:block"
            imageClassName="scale-x-[-1]"
          />
          <BackgroundImage
            src="/email-capture/below-modal.webp"
            alt=""
            className="absolute top-1/4 lg:right-8 xl:right-1/4 w-36 h-36 lg:w-40 lg:h-40 hidden lg:block"
          />
          <BackgroundImage
            src="/email-capture/left-bottom.webp"
            alt=""
            className="absolute bottom-40 lg:left-12 xl:left-32 w-32 lg:w-36 h-32 lg:h-36 hidden lg:block"
            hasBlur={true}
            imageClassName="blur-sm"
          />
          <BackgroundImage
            src="/email-capture/bottomright-below.webp"
            alt=""
            className="absolute bottom-20 lg:right-8 xl:right-24 w-32 lg:w-36 h-32 lg:h-36 hidden lg:block"
          />
          <BackgroundImage
            src="/email-capture/bottom-right.webp"
            alt=""
            className="absolute bottom-32 lg:right-16 xl:right-48 w-36 h-36 lg:w-40 lg:h-40 hidden lg:block"
          />

          {/* Main content area */}
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-sm sm:max-w-md mx-auto">
              {/* Main modal card */}
              <div className="bg-white shadow-lg rounded-3xl p-6 sm:p-8 lg:p-10 text-center relative z-10">
                {/* Logo - only visible on mobile, inside card */}
                <div className="mb-6 sm:hidden flex justify-center">
                  <LogoImage className="w-24" href={homepageUrl} />
                </div>

                {/* Title - lighter font weight and color */}
                <h1 className="text-2xl sm:text-2xl lg:text-3xl font-light text-gray-700 mb-3 leading-tight">
                  {t("emailCapture.title")}
                </h1>

                <p className="text-gray-500 mb-6 text-base font-light">
                  {t("emailCapture.subtitle")}
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <input
                      type="email"
                      placeholder={t("emailCapture.placeholder")}
                      value={email}
                      onChange={handleEmailChange}
                      className={`w-full border ${showError ? "border-red-500" : "border-gray-300"} rounded-md px-4 py-3 focus:outline-none focus:ring-2 ${showError ? "focus:ring-red-500" : "focus:ring-gray-400"} text-base font-light text-gray-700`}
                    />
                    {showError && (
                      <p className="text-red-500 text-sm mt-2 text-left">
                        {t("emailCapture.invalidEmail")}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-br from-[#4C2652] to-[#5C4279] hover:shadow-md text-white py-3 rounded-md font-normal mb-4 text-base transition-all flex items-center justify-center gap-2"
                  >
                    {t("common.getStarted")}
                    <ArrowRight className="h-4 w-4" />
                  </button>

                  {/* Skip option under the button */}
                  {onSkip && (
                    <div className="mb-8">
                      <div
                        data-testid="skip-email-capture"
                        onClick={handleSkip}
                        className="text-sm text-gray-400 hover:text-gray-600 underline transition-colors cursor-pointer text-center"
                      >
                        {t("common.skip")}
                      </div>
                    </div>
                  )}
                </form>

                {/* Feature icons - lighter text */}
                {useMemo(
                  () => (
                    <div className="flex flex-col space-y-6 sm:space-y-0 sm:flex-row sm:justify-between text-gray-400 mb-8">
                      <div className="flex flex-col items-center sm:w-1/3 px-2">
                        <CardiogrammIcon className="w-6 h-6 mb-2" />
                        <p className="text-sm leading-tight font-light text-center">
                          {t("emailCapture.feature1")}
                        </p>
                      </div>
                      <div className="flex flex-col items-center sm:w-1/3 px-2">
                        <StethoscopeIcon className="w-6 h-6 mb-2" />
                        <p className="text-sm leading-tight font-light text-center">
                          {t("emailCapture.feature2")}
                        </p>
                      </div>
                      <div className="flex flex-col items-center sm:w-1/3 px-2">
                        <PlanIcon className="w-6 h-6 mb-2" />
                        <p className="text-sm leading-tight font-light text-center">
                          {t("emailCapture.feature3")}
                        </p>
                      </div>
                    </div>
                  ),
                  [language],
                )}

                {supportPhone && (
                  <div className="flex items-center justify-center space-x-2 text-gray-600 font-light">
                    <LiveBadge language={language} phone={supportPhone} />
                  </div>
                )}
              </div>
            </div>
          </main>

          {/* Footer - lighter text */}
          <footer className="py-6 text-center text-xs sm:text-sm text-gray-400 font-light">
            {/* Payment logos */}
            <div className="flex flex-row justify-center items-start gap-4 mb-4">
              {/* Klarna */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="#FC81A0" />
                <path
                  d="M9.63 8.40002H8.055C8.055 9.69179 7.462 10.8706 6.43 11.6471L5.809 12.113L8.224 15.4024H10.209L7.984 12.3741C9.037 11.3294 9.63 9.91767 9.63 8.40002Z"
                  fill="white"
                />
                <path
                  d="M5.611 8.40002H4V15.4024H5.611V8.40002Z"
                  fill="white"
                />
                <path
                  d="M12.195 8.40002H10.676V15.4024H12.195V8.40002Z"
                  fill="white"
                />
                <path
                  d="M25.517 10.4259C24.938 10.4259 24.387 10.6024 24.019 11.1036V10.56H22.578V15.4024H24.04V12.8612C24.04 12.1271 24.535 11.7671 25.128 11.7671C25.764 11.7671 26.131 12.1483 26.131 12.8541V15.4094H27.579V12.3247C27.579 11.1953 26.682 10.4259 25.517 10.4259Z"
                  fill="white"
                />
                <path
                  d="M16.786 10.56V10.8706C16.377 10.5883 15.882 10.4259 15.345 10.4259C13.932 10.4259 12.788 11.5694 12.788 12.9812C12.788 14.393 13.932 15.5365 15.345 15.5365C15.882 15.5365 16.377 15.3741 16.786 15.0918V15.4024H18.234V10.56H16.786ZM15.465 14.2236C14.738 14.2236 14.151 13.6659 14.151 12.9812C14.151 12.2965 14.738 11.7388 15.465 11.7388C16.193 11.7388 16.779 12.2965 16.779 12.9812C16.779 13.6659 16.193 14.2236 15.465 14.2236Z"
                  fill="white"
                />
                <path
                  d="M20.481 11.1882V10.56H18.997V15.4024H20.488V13.1435C20.488 12.3812 21.314 11.9718 21.886 11.9718C21.893 11.9718 21.9 11.9718 21.9 11.9718V10.56C21.314 10.56 20.77 10.8141 20.481 11.1882Z"
                  fill="white"
                />
                <path
                  d="M32.086 10.56V10.8706C31.676 10.5883 31.182 10.4259 30.645 10.4259C29.232 10.4259 28.088 11.5694 28.088 12.9812C28.088 14.393 29.232 15.5365 30.645 15.5365C31.182 15.5365 31.676 15.3741 32.086 15.0918V15.4024H33.534V10.56H32.086ZM30.765 14.2236C30.038 14.2236 29.451 13.6659 29.451 12.9812C29.451 12.2965 30.038 11.7388 30.765 11.7388C31.493 11.7388 32.079 12.2965 32.079 12.9812C32.086 13.6659 31.493 14.2236 30.765 14.2236Z"
                  fill="white"
                />
                <path
                  d="M34.658 10.7013C34.658 10.6306 34.608 10.5883 34.531 10.5883H34.396V10.9556H34.46V10.8214H34.531L34.587 10.9556H34.658L34.594 10.8072C34.637 10.786 34.658 10.7507 34.658 10.7013ZM34.531 10.7578H34.46V10.6448H34.531C34.573 10.6448 34.594 10.666 34.594 10.7013C34.594 10.7366 34.58 10.7578 34.531 10.7578Z"
                  fill="white"
                />
                <path
                  d="M34.509 10.433C34.318 10.433 34.163 10.5883 34.163 10.7789C34.17 10.9695 34.318 11.1247 34.509 11.1247C34.7 11.1247 34.855 10.9695 34.855 10.7789C34.855 10.5883 34.7 10.433 34.509 10.433ZM34.509 11.0612C34.354 11.0612 34.234 10.9342 34.234 10.7789C34.234 10.6236 34.361 10.4965 34.509 10.4965C34.665 10.4965 34.785 10.6236 34.785 10.7789C34.785 10.9342 34.658 11.0612 34.509 11.0612Z"
                  fill="white"
                />
                <path
                  d="M35.082 13.6871C34.581 13.6871 34.171 14.0965 34.171 14.5977C34.171 15.0988 34.581 15.5082 35.082 15.5082C35.584 15.5082 35.993 15.0988 35.993 14.5977C35.993 14.0894 35.584 13.6871 35.082 13.6871Z"
                  fill="white"
                />
              </svg>
              {/* Maestro */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="#10427A" />
                <path
                  d="M31.123 18.5002C31.204 18.5002 31.321 18.5152 31.41 18.5492L31.285 18.9177C31.2 18.8837 31.115 18.8725 31.033 18.8725C30.77 18.8725 30.638 19.0378 30.638 19.3347V20.3428H30.234V18.5454H30.634V18.7636C30.739 18.6056 30.89 18.5002 31.123 18.5002ZM29.632 18.9026H28.972V19.7148C28.972 19.8951 29.038 20.0157 29.24 20.0157C29.345 20.0157 29.477 19.9817 29.597 19.9141L29.713 20.2485C29.585 20.335 29.383 20.388 29.209 20.388C28.732 20.388 28.565 20.1396 28.565 19.7221V18.9026H28.189V18.5454H28.565V18H28.972V18.5454H29.632V18.9026ZM24.469 19.2861C24.512 19.0266 24.674 18.8497 24.961 18.8497C25.221 18.8497 25.388 19.0076 25.431 19.2861H24.469ZM25.85 19.4441C25.846 18.8837 25.489 18.5002 24.969 18.5002C24.426 18.5002 24.046 18.8837 24.046 19.4441C24.046 20.0152 24.442 20.3875 24.997 20.3875C25.276 20.3875 25.532 20.32 25.757 20.1357L25.559 19.8461C25.404 19.9666 25.206 20.0342 25.02 20.0342C24.76 20.0342 24.523 19.9175 24.465 19.5943H25.842C25.846 19.5452 25.85 19.4966 25.85 19.4441ZM27.622 19.0042C27.51 18.9362 27.281 18.8497 27.045 18.8497C26.824 18.8497 26.692 18.9289 26.692 19.0606C26.692 19.1807 26.831 19.2147 27.006 19.237L27.196 19.2633C27.599 19.3201 27.843 19.4854 27.843 19.8013C27.843 20.1435 27.533 20.388 26.998 20.388C26.695 20.388 26.416 20.3127 26.195 20.1547L26.385 19.85C26.521 19.9515 26.723 20.038 27.002 20.038C27.277 20.038 27.425 19.9593 27.425 19.8198C27.425 19.7187 27.32 19.6619 27.099 19.6322L26.909 19.6059C26.494 19.5491 26.269 19.3687 26.269 19.0756C26.269 18.7184 26.571 18.5002 27.041 18.5002C27.335 18.5002 27.603 18.5643 27.797 18.6883L27.622 19.0042ZM32.597 18.8716C32.514 18.8716 32.437 18.8858 32.366 18.9138C32.295 18.9422 32.233 18.9818 32.181 19.0326C32.129 19.0834 32.088 19.1445 32.058 19.2155C32.029 19.2865 32.014 19.3649 32.014 19.4501C32.014 19.5357 32.029 19.6137 32.058 19.6847C32.088 19.7557 32.129 19.8168 32.181 19.8676C32.233 19.9184 32.295 19.958 32.366 19.9864C32.437 20.0148 32.514 20.0286 32.597 20.0286C32.68 20.0286 32.757 20.0148 32.828 19.9864C32.9 19.958 32.962 19.9184 33.014 19.8676C33.067 19.8168 33.108 19.7557 33.138 19.6847C33.168 19.6137 33.182 19.5357 33.182 19.4501C33.182 19.3649 33.168 19.2865 33.138 19.2155C33.108 19.1445 33.067 19.0834 33.014 19.0326C32.962 18.9818 32.9 18.9422 32.828 18.9138C32.757 18.8858 32.68 18.8716 32.597 18.8716ZM32.597 18.5002C32.741 18.5002 32.874 18.5243 32.997 18.5729C33.119 18.6211 33.225 18.6878 33.314 18.7726C33.404 18.8574 33.474 18.9577 33.524 19.0731C33.575 19.1888 33.6 19.3145 33.6 19.4501C33.6 19.5857 33.575 19.7114 33.524 19.8271C33.474 19.9425 33.404 20.0432 33.314 20.128C33.225 20.2128 33.119 20.2791 32.997 20.3277C32.874 20.3759 32.741 20.4 32.597 20.4C32.453 20.4 32.32 20.3759 32.197 20.3277C32.075 20.2791 31.97 20.2128 31.881 20.128C31.792 20.0432 31.722 19.9425 31.672 19.8271C31.621 19.7114 31.596 19.5857 31.596 19.4501C31.596 19.3145 31.621 19.1888 31.672 19.0731C31.722 18.9577 31.792 18.8574 31.881 18.7726C31.97 18.6878 32.075 18.6211 32.197 18.5729C32.32 18.5243 32.453 18.5002 32.597 18.5002ZM22.112 19.4441C22.112 19.1281 22.326 18.8686 22.675 18.8686C23.008 18.8686 23.233 19.117 23.233 19.4441C23.233 19.7712 23.008 20.0191 22.675 20.0191C22.326 20.0191 22.112 19.76 22.112 19.4441ZM23.613 19.4441V18.5454H23.21V18.7636C23.082 18.6017 22.888 18.5002 22.624 18.5002C22.104 18.5002 21.697 18.8949 21.697 19.4441C21.697 19.9929 22.104 20.388 22.624 20.388C22.888 20.388 23.082 20.2864 23.21 20.1246V20.3428H23.613V19.4441ZM21.34 20.3428V19.2147C21.34 18.7898 21.061 18.504 20.611 18.5002C20.375 18.4963 20.13 18.5677 19.96 18.82C19.832 18.6207 19.63 18.5002 19.347 18.5002C19.149 18.5002 18.955 18.5566 18.804 18.767V18.5454H18.4V20.3428H18.808V19.3464C18.808 19.0343 18.986 18.8686 19.261 18.8686C19.529 18.8686 19.665 19.0378 19.665 19.3425V20.3428H20.072V19.3464C20.072 19.0343 20.258 18.8686 20.526 18.8686C20.801 18.8686 20.933 19.0378 20.933 19.3425V20.3428H21.34Z"
                  fill="white"
                />
                <circle cx="22.5" cy="10.1" r="6.5" fill="#D32011" />
                <circle cx="30.5" cy="10.1" r="6.5" fill="#0099DF" />
                <circle
                  cx="22.5"
                  cy="10.1"
                  r="6.5"
                  fill="#D32011"
                  fillOpacity="0.4"
                />
              </svg>
              {/* Mastercard */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="#10427A" />
                <path
                  d="M24.914 20.3647V19.3059C24.914 18.9 24.648 18.6353 24.19 18.6353C23.962 18.6353 23.714 18.7059 23.543 18.9353C23.41 18.7412 23.219 18.6353 22.933 18.6353C22.743 18.6353 22.552 18.6882 22.4 18.8824V18.6706H22V20.3647H22.4V19.4294C22.4 19.1294 22.571 18.9882 22.838 18.9882C23.105 18.9882 23.238 19.1471 23.238 19.4294V20.3647H23.638V19.4294C23.638 19.1294 23.829 18.9882 24.076 18.9882C24.343 18.9882 24.476 19.1471 24.476 19.4294V20.3647H24.914ZM30.838 18.6706H30.19V18.1588H29.79V18.6706H29.429V19.0059H29.79V19.7824C29.79 20.1706 29.962 20.4 30.419 20.4C30.59 20.4 30.781 20.3471 30.914 20.2765L30.8 19.9588C30.686 20.0294 30.552 20.0471 30.457 20.0471C30.267 20.0471 30.19 19.9412 30.19 19.7647V19.0059H30.838V18.6706ZM34.229 18.6353C34 18.6353 33.848 18.7412 33.752 18.8824V18.6706H33.352V20.3647H33.752V19.4118C33.752 19.1294 33.886 18.9706 34.133 18.9706C34.21 18.9706 34.305 18.9882 34.381 19.0059L34.495 18.6529C34.419 18.6353 34.305 18.6353 34.229 18.6353ZM29.105 18.8118C28.914 18.6882 28.648 18.6353 28.362 18.6353C27.905 18.6353 27.6 18.8471 27.6 19.1824C27.6 19.4647 27.829 19.6235 28.229 19.6765L28.419 19.6941C28.629 19.7294 28.743 19.7824 28.743 19.8706C28.743 19.9941 28.59 20.0824 28.324 20.0824C28.057 20.0824 27.848 19.9941 27.714 19.9059L27.524 20.1882C27.733 20.3294 28.019 20.4 28.305 20.4C28.838 20.4 29.143 20.1706 29.143 19.8529C29.143 19.5529 28.895 19.3941 28.514 19.3412L28.324 19.3235C28.152 19.3059 28.019 19.2706 28.019 19.1647C28.019 19.0412 28.152 18.9706 28.362 18.9706C28.59 18.9706 28.819 19.0588 28.933 19.1118L29.105 18.8118ZM39.733 18.6353C39.505 18.6353 39.352 18.7412 39.257 18.8824V18.6706H38.857V20.3647H39.257V19.4118C39.257 19.1294 39.39 18.9706 39.638 18.9706C39.714 18.9706 39.81 18.9882 39.886 19.0059L40 18.6529C39.924 18.6353 39.81 18.6353 39.733 18.6353ZM34.629 19.5176C34.629 20.0294 35.01 20.4 35.6 20.4C35.867 20.4 36.057 20.3471 36.248 20.2059L36.057 19.9059C35.905 20.0118 35.752 20.0647 35.581 20.0647C35.257 20.0647 35.029 19.8529 35.029 19.5176C35.029 19.2 35.257 18.9882 35.581 18.9706C35.752 18.9706 35.905 19.0235 36.057 19.1294L36.248 18.8294C36.057 18.6882 35.867 18.6353 35.6 18.6353C35.01 18.6353 34.629 19.0059 34.629 19.5176ZM38.324 19.5176V18.6706H37.924V18.8824C37.79 18.7235 37.6 18.6353 37.352 18.6353C36.838 18.6353 36.438 19.0059 36.438 19.5176C36.438 20.0294 36.838 20.4 37.352 20.4C37.619 20.4 37.81 20.3118 37.924 20.1529V20.3647H38.324V19.5176ZM36.857 19.5176C36.857 19.2176 37.067 18.9706 37.41 18.9706C37.733 18.9706 37.962 19.2 37.962 19.5176C37.962 19.8176 37.733 20.0647 37.41 20.0647C37.067 20.0471 36.857 19.8176 36.857 19.5176ZM32.076 18.6353C31.543 18.6353 31.162 18.9882 31.162 19.5176C31.162 20.0471 31.543 20.4 32.095 20.4C32.362 20.4 32.629 20.3294 32.838 20.1706L32.648 19.9059C32.495 20.0118 32.305 20.0824 32.114 20.0824C31.867 20.0824 31.619 19.9765 31.562 19.6765H32.914V19.5353C32.933 18.9882 32.59 18.6353 32.076 18.6353ZM32.076 18.9529C32.324 18.9529 32.495 19.0941 32.533 19.3588H31.581C31.619 19.1294 31.79 18.9529 32.076 18.9529ZM42 19.5176V18H41.6V18.8824C41.467 18.7235 41.276 18.6353 41.029 18.6353C40.514 18.6353 40.114 19.0059 40.114 19.5176C40.114 20.0294 40.514 20.4 41.029 20.4C41.295 20.4 41.486 20.3118 41.6 20.1529V20.3647H42V19.5176ZM40.533 19.5176C40.533 19.2176 40.743 18.9706 41.086 18.9706C41.41 18.9706 41.638 19.2 41.638 19.5176C41.638 19.8176 41.41 20.0647 41.086 20.0647C40.743 20.0471 40.533 19.8176 40.533 19.5176ZM27.162 19.5176V18.6706H26.762V18.8824C26.629 18.7235 26.438 18.6353 26.19 18.6353C25.676 18.6353 25.276 19.0059 25.276 19.5176C25.276 20.0294 25.676 20.4 26.19 20.4C26.457 20.4 26.648 20.3118 26.762 20.1529V20.3647H27.162V19.5176ZM25.676 19.5176C25.676 19.2176 25.886 18.9706 26.229 18.9706C26.552 18.9706 26.781 19.2 26.781 19.5176C26.781 19.8176 26.552 20.0647 26.229 20.0647C25.886 20.0471 25.676 19.8176 25.676 19.5176Z"
                  fill="white"
                />
                <circle cx="27.9" cy="10.1" r="6.5" fill="#D32011" />
                <circle cx="35.9" cy="10.1" r="6.5" fill="#F79E1B" />
                <circle
                  cx="27.9"
                  cy="10.1"
                  r="6.5"
                  fill="#EB001B"
                  fillOpacity="0.3"
                />
              </svg>
              {/* PayPal */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="#B9E8FF" />
                <path
                  d="M24.735 5.20219C23.995 4.36074 22.657 4 20.945 4H15.977C15.627 4 15.329 4.25402 15.274 4.59845L13.206 17.6827C13.164 17.9407 13.365 18.1744 13.627 18.1744H16.694L17.465 13.3017L17.441 13.4543C17.495 13.1099 17.791 12.8559 18.141 12.8559H19.598C22.462 12.8559 24.704 11.696 25.358 8.34084C25.378 8.24161 25.395 8.14503 25.409 8.05065C25.327 8.00699 25.327 8.00699 25.409 8.05065C25.604 6.81054 25.408 5.96645 24.735 5.20219Z"
                  fill="#27346A"
                />
                <path
                  d="M18.638 7.60391C18.72 7.5651 18.811 7.54349 18.907 7.54349H22.802C23.263 7.54349 23.693 7.57348 24.086 7.63655C24.199 7.65463 24.308 7.67536 24.414 7.69917C24.52 7.72254 24.624 7.749 24.723 7.77811C24.773 7.79266 24.823 7.80766 24.871 7.82353C25.064 7.88792 25.244 7.96289 25.409 8.05065C25.604 6.8101 25.408 5.96645 24.735 5.20219C23.995 4.36074 22.657 4 20.945 4H15.976C15.627 4 15.329 4.25402 15.274 4.59845L13.206 17.6823C13.164 17.9407 13.365 18.174 13.627 18.174H16.694L18.292 8.06697C18.325 7.8597 18.459 7.68991 18.638 7.60391Z"
                  fill="#27346A"
                />
                <path
                  d="M25.358 8.34084C24.703 11.6956 22.461 12.8559 19.598 12.8559H18.14C17.79 12.8559 17.495 13.1099 17.44 13.4543L16.482 19.5129C16.446 19.7387 16.621 19.9433 16.851 19.9433H19.436C19.742 19.9433 20.003 19.721 20.05 19.4198L20.076 19.2884L20.563 16.2084L20.594 16.0382C20.642 15.737 20.903 15.5147 21.209 15.5147H21.596C24.1 15.5147 26.061 14.5 26.635 11.565C26.874 10.3386 26.75 9.31459 26.117 8.5953C25.925 8.37745 25.687 8.19751 25.409 8.05066C25.394 8.14548 25.378 8.24162 25.358 8.34084Z"
                  fill="#2790C3"
                />
                <path
                  d="M24.724 7.77813C24.624 7.74903 24.521 7.72257 24.415 7.69919C24.309 7.67582 24.199 7.65509 24.087 7.63701C23.693 7.57351 23.264 7.54352 22.802 7.54352H18.907C18.811 7.54352 18.72 7.56513 18.639 7.60438C18.459 7.69037 18.326 7.85972 18.293 8.06744L17.465 13.3018L17.441 13.4543C17.496 13.1099 17.791 12.8559 18.141 12.8559H19.599C22.462 12.8559 24.704 11.696 25.359 8.34086C25.379 8.24163 25.395 8.14549 25.41 8.05068C25.244 7.96336 25.065 7.88795 24.871 7.824C24.823 7.80812 24.774 7.79269 24.724 7.77813Z"
                  fill="#1F264F"
                />
              </svg>
              {/* Apple Pay */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="white" />
                <path
                  d="M15.496 7.8182C15.182 8.18975 14.68 8.4828 14.177 8.44094C14.114 7.93856 14.36 7.40478 14.648 7.0751C14.963 6.69308 15.512 6.42096 15.957 6.40002C16.01 6.92334 15.805 7.43618 15.496 7.8182ZM15.952 8.54037C15.224 8.4985 14.601 8.95378 14.256 8.95378C13.905 8.95378 13.376 8.5613 12.8 8.57177C12.052 8.58223 11.356 9.00611 10.973 9.68119C10.188 11.0313 10.769 13.0304 11.528 14.1293C11.9 14.6736 12.345 15.2701 12.931 15.2492C13.486 15.2283 13.706 14.8881 14.376 14.8881C15.052 14.8881 15.245 15.2492 15.832 15.2387C16.439 15.2283 16.821 14.6945 17.193 14.1503C17.617 13.5328 17.789 12.9309 17.8 12.8995C17.789 12.8891 16.627 12.4443 16.617 11.1046C16.606 9.98471 17.533 9.45093 17.575 9.41953C17.051 8.64503 16.235 8.5613 15.952 8.54037ZM20.156 7.02276V15.1812H21.422V12.3919H23.176C24.778 12.3919 25.904 11.293 25.904 9.70212C25.904 8.11125 24.799 7.02276 23.218 7.02276H20.156ZM21.422 8.09032H22.883C23.982 8.09032 24.611 8.67643 24.611 9.70735C24.611 10.7383 23.982 11.3296 22.878 11.3296H21.422V8.09032ZM28.217 15.244C29.013 15.244 29.751 14.841 30.086 14.2026H30.113V15.1812H31.285V11.1203C31.285 9.94284 30.343 9.18404 28.893 9.18404C27.547 9.18404 26.553 9.95331 26.516 11.0104H27.657C27.752 10.508 28.217 10.1783 28.856 10.1783C29.631 10.1783 30.065 10.5394 30.065 11.204V11.6541L28.484 11.7483C27.013 11.8372 26.218 12.439 26.218 13.4857C26.218 14.5427 27.04 15.244 28.217 15.244ZM28.558 14.2759C27.882 14.2759 27.453 13.9514 27.453 13.4543C27.453 12.9414 27.867 12.6431 28.657 12.596L30.065 12.5071V12.9676C30.065 13.7316 29.416 14.2759 28.558 14.2759ZM32.85 17.4C34.086 17.4 34.667 16.929 35.175 15.5004L37.4 9.26254H36.112L34.62 14.0822H34.594L33.102 9.26254H31.777L33.924 15.2021L33.808 15.5632C33.615 16.1755 33.301 16.411 32.74 16.411C32.641 16.411 32.447 16.4005 32.369 16.39V17.3686C32.442 17.3896 32.756 17.4 32.85 17.4Z"
                  fill="black"
                />
              </svg>
              {/* Visa */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="#25459A" />
                <path
                  d="M20.453 10.5576C20.438 11.6719 21.512 12.2938 22.32 12.6636C23.151 13.043 23.43 13.2862 23.427 13.6254C23.42 14.1447 22.764 14.3738 22.15 14.3827C21.078 14.3984 20.455 14.1112 19.959 13.894L19.573 15.5894C20.07 15.8044 20.991 15.9918 21.945 16C24.185 16 25.651 14.9622 25.659 13.3532C25.668 11.3111 22.649 11.1981 22.67 10.2853C22.677 10.0086 22.958 9.71322 23.575 9.63809C23.88 9.60015 24.723 9.57114 25.678 9.98401L26.053 8.34369C25.54 8.16812 24.879 8 24.057 8C21.948 8 20.465 9.05189 20.453 10.5576ZM29.656 8.14134C29.247 8.14134 28.902 8.36526 28.749 8.70895L25.548 15.8795H27.787L28.232 14.7242H30.968L31.227 15.8795H33.2L31.478 8.14134H29.656ZM29.969 10.2317L30.615 13.1374H28.846L29.969 10.2317ZM17.739 8.14134L15.974 15.8795H18.108L19.871 8.14134H17.739ZM14.583 8.14134L12.362 13.4082L11.464 8.92989C11.359 8.42998 10.943 8.14134 10.481 8.14134H6.851L6.8 8.366C7.545 8.51776 8.392 8.76251 8.905 9.02436C9.218 9.1843 9.308 9.32416 9.411 9.7043L11.112 15.8795H13.367L16.823 8.14134H14.583Z"
                  fill="white"
                />
              </svg>
              {/* Google Pay */}
              <svg
                width="40"
                height="24"
                viewBox="0 0 40 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="40" height="24" rx="4" fill="white" />
                <path
                  d="M21.267 11.7927V14.9072H20.263V7.20459H22.875C23.511 7.20459 24.114 7.43902 24.583 7.87438C25.052 8.27626 25.286 8.87907 25.286 9.51537C25.286 10.1517 25.052 10.721 24.583 11.1564C24.114 11.5917 23.545 11.8261 22.875 11.8261L21.267 11.7927ZM21.267 8.1423V10.8215H22.942C23.31 10.8215 23.679 10.6875 23.913 10.4196C24.449 9.91724 24.449 9.08 23.947 8.57766L23.913 8.54417C23.645 8.27626 23.31 8.10881 22.942 8.1423H21.267Z"
                  fill="#5F6368"
                />
                <path
                  d="M27.597 9.48187C28.334 9.48187 28.903 9.68281 29.338 10.0847C29.774 10.4866 29.975 11.0224 29.975 11.6922V14.9072H29.037V14.1704H29.003C28.602 14.7732 28.032 15.0746 27.362 15.0746C26.793 15.0746 26.291 14.9072 25.889 14.5723C25.521 14.2374 25.286 13.7685 25.286 13.2662C25.286 12.7304 25.487 12.295 25.889 11.9601C26.291 11.6252 26.86 11.4912 27.53 11.4912C28.133 11.4912 28.602 11.5917 28.97 11.8261V11.5917C28.97 11.2568 28.836 10.9219 28.568 10.721C28.3 10.4866 27.965 10.3526 27.597 10.3526C27.028 10.3526 26.592 10.587 26.291 11.0559L25.42 10.52C25.956 9.81677 26.659 9.48187 27.597 9.48187ZM26.324 13.2997C26.324 13.5676 26.458 13.802 26.659 13.936C26.894 14.1034 27.162 14.2039 27.429 14.2039C27.831 14.2039 28.233 14.0365 28.535 13.735C28.87 13.4336 29.037 13.0653 29.037 12.6634C28.736 12.429 28.3 12.295 27.731 12.295C27.329 12.295 26.994 12.3955 26.726 12.5964C26.458 12.7638 26.324 12.9983 26.324 13.2997Z"
                  fill="#5F6368"
                />
                <path
                  d="M35.399 9.64935L32.084 17.2515H31.079L32.318 14.6058L30.142 9.68284H31.213L32.787 13.4672H32.821L34.361 9.68284H35.399V9.64935Z"
                  fill="#5F6368"
                />
                <path
                  d="M17.281 11.1229C17.281 10.8215 17.248 10.5201 17.215 10.2187H13.028V11.9267H15.406C15.306 12.4625 15.004 12.9648 14.535 13.2662V14.3714H15.975C16.813 13.6011 17.281 12.4625 17.281 11.1229Z"
                  fill="#4285F4"
                />
                <path
                  d="M13.029 15.443C14.235 15.443 15.239 15.0411 15.976 14.3713L14.536 13.2662C14.134 13.5341 13.632 13.7015 13.029 13.7015C11.89 13.7015 10.886 12.9313 10.551 11.8596H9.077V12.9983C9.847 14.5053 11.354 15.443 13.029 15.443Z"
                  fill="#34A853"
                />
                <path
                  d="M10.55 11.8596C10.349 11.3238 10.349 10.721 10.55 10.1516V9.013H9.077C8.441 10.2521 8.441 11.7257 9.077 12.9983L10.55 11.8596Z"
                  fill="#FBBC04"
                />
                <path
                  d="M13.029 8.34323C13.665 8.34323 14.268 8.57766 14.737 9.01302L16.009 7.74042C15.205 7.00365 14.134 6.56829 13.062 6.60178C11.388 6.60178 9.847 7.53948 9.11 9.04651L10.584 10.1852C10.885 9.11349 11.89 8.34323 13.029 8.34323Z"
                  fill="#EA4335"
                />
              </svg>
            </div>
            {/* Footer links */}
            <div className="space-x-4">
              <a
                href={homepageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                ©{new Date().getFullYear()} Ahead Health
              </a>
              <a
                href="https://www.aheadhealth.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                {t("footer.privacyPolicy")}
              </a>
              <a
                href="https://www.aheadhealth.com/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                {t("footer.termsConditions")}
              </a>
            </div>
          </footer>
        </div>
      </div>
    );
  },
);

EmailCaptureStep.displayName = "EmailCaptureStep";

export default EmailCaptureStep;
